export interface ResolveUserDetailsTargetInput {
  /** The `org_id` segment of the current route, if the route has one. */
  routeOrgId: string | undefined;
  /** The `user_id` segment of the current route, if the route has one. */
  routeUserId: string | undefined;
  /** The org the parent page resolved, if it resolves one. */
  orgIdProp: string | undefined;
  /** The user the parent page resolved, if it resolves one. */
  userIdProp: string | undefined;
  /** True on the My Account surface (PSTUDIO-4095). */
  isMyAccount: boolean;
}

export interface UserDetailsTarget {
  orgId: string;
  userId: string;
}

/**
 * Decide which org and user the user-details surface reads and WRITES.
 *
 * Two surfaces render `UserDetails`, and they disagree about where the ids may
 * come from:
 *
 * - `/organization/:org_id/users/:user_id` — the URL names both, and naming
 *   another user there is the whole point of the page.
 * - `/organization/:org_id/my-account` — the ids come from the session
 *   (`resolveMyAccountTarget` reads them off `ctx.user`, never off the URL),
 *   and the `:org_id` segment exists only because the surface lives under the
 *   org-scoped layout.
 *
 * A route-first rule (`routeOrgId || orgIdProp`) is not merely redundant on the
 * second surface, it is wrong there: that path ALWAYS carries an `org_id`, so
 * the URL always wins. `RouteDataProvider` (`with-route-data.tsx`) redirects
 * away only from a segment it cannot find in `ctx.orgs` — and `ctx.orgs` is
 * assembled in `manager.xstate.ts` as the default account CONCATENATED WITH
 * `allChildAccounts`, so it holds the user's home org plus every child org
 * beneath it, not the orgs they are a member of. Any user whose home org has
 * children can therefore sit on `/organization/<a child>/my-account` without
 * being redirected. `UserDetailsPage` would still LOAD the home org (it passes
 * the resolved prop to `manager.loadUserDetails`), while this component's
 * `targetOrgId` — which feeds the avatar upload's `accountId`,
 * `manager.userDetails.saveUpdates` and `manager.deleteUser` — would carry the
 * org from the URL. Reads and writes would name different orgs.
 *
 * So on My Account the route is ignored outright, both segments of it.
 */
export const resolveUserDetailsTarget = ({
  routeOrgId,
  routeUserId,
  orgIdProp,
  userIdProp,
  isMyAccount,
}: ResolveUserDetailsTargetInput): UserDetailsTarget =>
  isMyAccount
    ? { orgId: orgIdProp || '', userId: userIdProp || '' }
    : {
        orgId: routeOrgId || orgIdProp || '',
        userId: routeUserId || userIdProp || '',
      };
